/**
 * @file Détecte les nouveaux déploiements et recharge automatiquement l'application.
 * @module apps.dashboard.src.pwa.hooks.useAutoUpdate
 *
 * Les PWA installées sur tablette restent ouvertes des semaines : le bundle
 * chargé en mémoire ne se met jamais à jour tout seul après un déploiement
 * (le service worker est pass-through, il ne cache rien — le problème est le
 * JS déjà exécuté, pas un cache). Résultat : un frontend obsolète parle à un
 * backend à jour, avec des incohérences de contrat (ex. rejet 500 d'un
 * `waiting_customer` que le nouveau front n'enverrait plus).
 *
 * Ce hook compare la version embarquée dans le bundle
 * (`REACT_APP_BUILD_VERSION`, injectée au build par craco.config.js) au
 * `version.json` publié à la racine à chaque build, puis recharge l'app à un
 * moment sûr — jamais au milieu d'une saisie :
 *
 *  - au montage, si l'index.html servi provenait d'un cache HTTP périmé ;
 *  - au réveil de la tablette après une longue veille (≥ 30 min) — cas nominal ;
 *  - à un changement de route, si une mise à jour est en attente ;
 *  - sur échec de chargement d'un chunk lazy (les anciens chunks hashés
 *    disparaissent du serveur après un déploiement) — filet de sécurité.
 *
 * La vérification périodique ne recharge jamais immédiatement : elle marque
 * la mise à jour « en attente » et laisse un déclencheur sûr l'appliquer.
 * Un garde-fou sessionStorage empêche toute boucle de rechargement si le
 * serveur continue de servir l'ancien bundle (cache CDN en retard).
 */

import {useEffect, useRef, useCallback} from 'react';
import {useLocation} from 'react-router-dom';

const CHECK_INTERVAL_MS = 15 * 60 * 1000;
const RELOAD_GUARD_KEY = 'brydge-auto-update-attempted-version';

// Durée de veille minimale pour autoriser un rechargement immédiat au réveil.
// En-dessous (verrouillage bref en plein service), une saisie est peut-être en
// cours : on marque la mise à jour en attente au lieu de risquer de la perdre.
const MIN_HIDDEN_FOR_IMMEDIATE_MS = 30 * 60 * 1000;

const CURRENT_VERSION = process.env.REACT_APP_BUILD_VERSION || null;
const IS_ENABLED = process.env.NODE_ENV === 'production' && Boolean(CURRENT_VERSION);

/** Motifs d'erreur webpack signalant un chunk devenu introuvable après déploiement. */
const CHUNK_ERROR_PATTERN = /ChunkLoadError|Loading (CSS )?chunk [\S]+ failed/i;

/**
 * Vérifie qu'un rechargement n'a pas déjà été tenté pour cette version cible.
 * Empêche une boucle infinie si le serveur sert encore l'ancien bundle.
 *
 * @param {string} remoteVersion - Version annoncée par version.json
 * @return {boolean} true si un rechargement a déjà été tenté pour cette version
 */
const alreadyAttempted = (remoteVersion) => {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY) === remoteVersion;
  } catch (error) {
    return false;
  }
};

/**
 * Marque la version cible comme tentée puis recharge le document complet,
 * ce qui récupère index.html et le nouveau bundle.
 *
 * @param {string} remoteVersion - Version vers laquelle on se met à jour
 */
const applyUpdate = (remoteVersion) => {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, remoteVersion);
  } catch (error) {
    // sessionStorage indisponible : on recharge quand même, le garde-fou saute
  }
  window.location.reload();
};

/**
 * Surveille les déploiements et recharge l'application quand une nouvelle
 * version est disponible. Doit être monté une seule fois, sous le Router
 * (dépend de useLocation pour le déclencheur « changement de route »).
 *
 * No-op complet en développement ou si la version de build n'est pas injectée.
 *
 * @return {void}
 */
const useAutoUpdate = () => {
  const location = useLocation();
  const pendingVersionRef = useRef(null);
  const inFlightRef = useRef(false);

  /**
   * Compare la version du bundle à celle du serveur. En mode immédiat
   * (moment sûr), recharge sur-le-champ ; sinon marque la mise à jour en
   * attente pour le prochain déclencheur sûr.
   */
  const checkForUpdate = useCallback(async ({immediate}) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      // Query param anti-cache : contourne le cache HTTP/CDN sans dépendre
      // des headers du hosting. cache: no-store en ceinture-bretelles.
      const response = await fetch(`/version.json?t=${Date.now()}`, {cache: 'no-store'});
      if (!response.ok) return;
      const data = await response.json();
      const remoteVersion = data?.version;
      if (!remoteVersion || remoteVersion === CURRENT_VERSION || alreadyAttempted(remoteVersion)) {
        return;
      }
      if (immediate) {
        applyUpdate(remoteVersion);
      } else {
        pendingVersionRef.current = remoteVersion;
      }
    } catch (error) {
      // Réseau indisponible ou version.json absent (fallback SPA renvoyant du
      // HTML) : silencieux, la prochaine vérification retentera.
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // Déclencheurs : montage, cycle de visibilité (réveil/mise en veille de la
  // tablette), retour du réseau, et vérification périodique de fond.
  useEffect(() => {
    if (!IS_ENABLED) return undefined;

    checkForUpdate({immediate: true});

    let hiddenAt = null;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      // Réveil : rechargement immédiat seulement après une longue veille —
      // une veille courte peut interrompre une saisie en cours, qu'un reload
      // détruirait. Le cas différé est appliqué au prochain changement de route.
      const longSleep = hiddenAt === null || Date.now() - hiddenAt >= MIN_HIDDEN_FOR_IMMEDIATE_MS;
      checkForUpdate({immediate: longSleep});
    };

    const onOnline = () => checkForUpdate({immediate: false});

    const intervalId = setInterval(() => checkForUpdate({immediate: false}), CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
    };
  }, [checkForUpdate]);

  // Mise à jour en attente + navigation : la route vient de changer, aucune
  // saisie en cours, on applique. L'URL cible est conservée par le reload.
  useEffect(() => {
    if (!IS_ENABLED) return;
    if (pendingVersionRef.current) {
      applyUpdate(pendingVersionRef.current);
    }
  }, [location.pathname]);

  // Filet de sécurité : un chunk lazy introuvable signifie presque toujours
  // qu'un déploiement a supprimé les anciens fichiers hashés. Sans cela,
  // l'utilisateur reste bloqué sur une page blanche jusqu'à un reload manuel.
  useEffect(() => {
    if (!IS_ENABLED) return undefined;

    const onChunkError = (event) => {
      const message = event?.reason?.message || event?.message || '';
      const name = event?.reason?.name || event?.error?.name || '';
      if (!CHUNK_ERROR_PATTERN.test(`${name} ${message}`)) return;
      if (alreadyAttempted('chunk-error')) return;
      applyUpdate('chunk-error');
    };

    window.addEventListener('error', onChunkError);
    window.addEventListener('unhandledrejection', onChunkError);

    return () => {
      window.removeEventListener('error', onChunkError);
      window.removeEventListener('unhandledrejection', onChunkError);
    };
  }, []);
};

export default useAutoUpdate;
